import { paraSlug, slugsUnicos } from '@nyroh/contracts';

import type { LeadJSON } from '../types';
import { api } from './api';

/**
 * ============================================================================
 * A ESTEIRA PELA FILA — o trabalho pesado sai do navegador
 * ============================================================================
 *
 * O QUE EXISTIA
 *
 * `MassGeneratorView` roda a esteira num laço com `setTimeout`, dentro da aba:
 * para cada lead, chama a IA, monta o HTML e salva. Isso significa que fechar a
 * aba mata o lote, que o progresso não sobrevive a um F5, e que 116 leads viram
 * 116 chamadas em série no navegador de uma pessoa.
 *
 * Era também a seção 14 do diagnóstico inteira por ligar: o worker sobe para
 * consumir uma fila que ninguém alimentava.
 *
 * O QUE ISTO FAZ
 *
 * Cria os clientes pela API e enfileira um `gerar_site` por cliente. O worker —
 * que já sabe processar esse tipo desde a primeira versão — pega, escolhe o
 * template, publica no R2 e vira o ponteiro da versão. Fechar a aba não
 * interrompe nada.
 *
 * IDEMPOTÊNCIA
 *
 * A chave é `gerar_site:<clienteId>`. Enfileirar o mesmo cliente duas vezes não
 * cria dois jobs, e reprocessar não gera site duplicado: o manipulador
 * reaproveita o site existente do cliente.
 */

export interface ResultadoEnfileiramento {
  /** Clientes criados agora. */
  criados: number;
  /** Ignorados porque o slug já existia no banco. */
  ignorados: number;
  /** Jobs efetivamente enfileirados. */
  enfileirados: number;
  /** Já havia job igual na fila (mesma chave de idempotência). */
  jaNaFila: number;
}

export interface LeadParaEsteira {
  leadData: LeadJSON;
  /** Força um template. Ausente = o worker escolhe pela categoria. */
  templateId?: string;
}

/**
 * Cria os clientes e enfileira a geração de cada um.
 *
 * `aoProgredir` existe para a tela poder mostrar andamento sem este módulo
 * conhecer React.
 */
export async function enfileirarEsteira(
  leads: LeadParaEsteira[],
  aoProgredir?: (feito: number, total: number) => void,
): Promise<ResultadoEnfileiramento> {
  const total = leads.length;
  const r: ResultadoEnfileiramento = { criados: 0, ignorados: 0, enfileirados: 0, jaNaFila: 0 };
  if (total === 0) return r;

  // Slugs únicos dentro do lote ANTES de enviar: dois nomes parecidos que
  // colidam depois de normalizados fariam o segundo sumir no
  // `ON CONFLICT DO NOTHING` do import, contado como "já existe".
  const slugs = slugsUnicos(leads.map((l) => l.leadData.name ?? 'projeto'));

  // Lotes de 500 — o teto que o contrato aceita por requisição.
  for (let i = 0; i < total; i += 500) {
    const fatia = leads.slice(i, i + 500);
    const slugsDaFatia = slugs.slice(i, i + 500);

    const corpo = fatia.map((l, k) => {
      const d = l.leadData;
      const telefone = d.phoneNumber ?? undefined;
      return {
        nome: d.name || slugsDaFatia[k]!,
        slug: slugsDaFatia[k]!,
        telefone,
        endereco: d.address ?? undefined,
        categoria: d.categories?.[0] ?? undefined,
        nota: typeof d.rating === 'number' ? d.rating : undefined,
        totalAvaliacoes: typeof d.reviewCount === 'number' ? d.reviewCount : undefined,
        status: 'pendente' as const,
        dadosNegocio: {
          resumo: d.summary ?? null,
          palavrasChaveSeo: d.seoKeywords ?? [],
          categorias: d.categories ?? [],
          website: d.website ?? null,
          horarios: d.openingHours ?? null,
          placeUri: d.placeUri ?? null,
        },
      };
    });

    const importado = await api.chamar('clientes.importar', { corpo: { clientes: corpo } });
    r.criados += importado.salvos;
    r.ignorados += importado.ignorados;

    /*
      O casamento é por SLUG, e não por posição — isto era um defeito real.

      A versão anterior percorria `idsCriados.entries()` e indexava `fatia[k]`
      pela mesma posição. `idsCriados` só traz quem NASCEU: um slug que já
      existia no meio do lote desloca tudo dali para a frente, e o
      `templateId` escolhido para um lead ia parar no cliente seguinte. Sem erro,
      sem log — o site saía com o layout de outro.
    */
    const porSlug = new Map(importado.criados.map((c) => [c.slug, c.id]));

    // Só os que nasceram agora entram na fila. Reenfileirar quem já existia
    // republicaria o site de um cliente que ninguém pediu para mexer.
    for (const [k, lead] of fatia.entries()) {
      const clienteId = porSlug.get(slugsDaFatia[k]!);
      if (!clienteId) continue;

      const job = await api.chamar('fila.enfileirar', {
        corpo: {
          tipo: 'gerar_site',
          payload: {
            clienteId,
            ...(lead.templateId ? { templateId: lead.templateId } : {}),
          },
          // Mesmo cliente, mesma chave: enfileirar duas vezes não vira dois jobs.
          chaveIdempotencia: `gerar_site:${clienteId}`,
          /*
            SEM `recurso`, e a decisão MUDOU DE MOTIVO — vale registrar os dois.

            O motivo antigo era que este job não gastava nada de terceiro: ele
            escolhia um template e publicava. Isso deixou de ser verdade — o
            manipulador agora chama a IA pela API.

            O motivo NOVO é o comportamento da fila: `recurso` sem cota cadastrada
            faz o job FALHAR (verificado — `Nenhuma cota definida`), e falhar cinco
            vezes o manda para o dead letter. Declarar `recurso: 'gemini'` aqui
            transformaria "esqueci de rodar `definir-cota`" em "a esteira parou e
            os leads estão numa fila que ninguém olha".

            Quem consulta a cota é a API, dentro de `ia.gerarSite`, onde "sem cota
            cadastrada" é uma decisão (roda sem teto, e a tela de IA diz isso em
            voz alta) e "cota estourada" é uma resposta — o job cai para o template
            e conclui com um site publicável, em vez de morrer.

            Ou seja: a cota continua existindo e continua sendo a mesma linha de
            `cotas_recurso`. O que não existe é a fila derrubando o lote por causa
            de uma configuração ausente.
          */
        },
      });
      if (job.novo) r.enfileirados++;
      else r.jaNaFila++;
    }

    aoProgredir?.(Math.min(i + fatia.length, total), total);
  }

  return r;
}

/** Enfileira a geração de UM cliente que já existe no banco. */
export async function enfileirarUmCliente(
  clienteId: string,
  templateId?: string,
): Promise<boolean> {
  const r = await api.chamar('fila.enfileirar', {
    corpo: {
      tipo: 'gerar_site',
      payload: { clienteId, ...(templateId ? { templateId } : {}) },
      chaveIdempotencia: `gerar_site:${clienteId}`,
      // Sem `recurso`: ver a explicação em `enfileirarEsteira`.
    },
  });
  return r.novo;
}

/**
 * O andamento da fila, para a tela mostrar sem inventar número.
 *
 * Vem do servidor: é a única fonte que sabe o que o worker já processou. A
 * esteira antiga contava no navegador, e o número morria com a aba.
 */
export async function resumoDaFila() {
  return api.chamar('fila.resumo', {});
}

/** Normaliza o nome como o servidor normaliza. Reexportado para a tela usar. */
export { paraSlug };
